import "./index.scss";
import { FC, useState } from "react";
import { Button, Form, Input, Modal, Table, Upload } from "antd";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { request } from "../../api/request";
import { AdminServerItem } from "../../constant";
import { ColumnsType, TablePaginationConfig } from "antd/es/table";
import { DeleteOutlined, EditOutlined, FileSearchOutlined, UploadOutlined } from "@ant-design/icons";

interface TableParams {
  pagination?: TablePaginationConfig;
}

enum EModalType {
  ADD,
  UPDATE,
}

const AdminServerManage: FC = () => {
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [isShowModal, setIsShowModal] = useState(false);
  const [modalType, setModalType] = useState<EModalType>(EModalType.ADD);

  const [tableParams, setTableParams] = useState<TableParams>({
    pagination: {
      current: 1,
      pageSize: 20,
    },
  });

  const [isShowImportModal, setIsShowImportModal] = useState(false);
  const [importingServerId, setImportingServerId] = useState<number | null>(null);
  const [csvData, setCsvData] = useState<string[][]>([]);

  const [checkingServerId, setCheckingServerId] = useState<number | null>(null);
  const [isShowIpListModal, setIsShowIpListModal] = useState(false);
  const [ipTableParams, setIpTableParams] = useState<TableParams>({
    pagination: {
      current: 1,
      pageSize: 20,
    },
  });

  /** 查询自有服务器列表 */
  const { data, isFetching, isError } = useQuery({
    queryKey: ["admin-server-list", { page: tableParams.pagination?.current, page_size: tableParams.pagination?.pageSize }],
    queryFn: async ({ queryKey }) => {
      const [_key, data] = queryKey;
      return request("manager/admin-server-list/", data);
    },

    placeholderData: keepPreviousData,
  });

  /** 新增 */
  const addServerMutation = useMutation({
    mutationFn: async (data: AdminServerItem) => {
      return request("manager/add-admin-server/", data);
    },
    onSuccess: (data) => {
      // queryClient.invalidateQueries({ queryKey: ["admin-server-list"] });
    },
    onSettled: (data, error, variables, context) => {
      setIsShowModal(false);
      queryClient.invalidateQueries({ queryKey: ["admin-server-list"] });
      form.resetFields();
    },
  });

  /** 编辑 */
  const updateServerMutation = useMutation({
    mutationFn: async (data: AdminServerItem) => {
      return request("manager/update-admin-server-detail/", data);
    },
    onSuccess: (data) => {
      // queryClient.invalidateQueries({ queryKey: ["admin-server-list"] });
    },
    onSettled: (data, error, variables, context) => {
      setIsShowModal(false);
      queryClient.invalidateQueries({ queryKey: ["admin-server-list"] });
      form.resetFields();
    },
  });

  /** 删除 */
  const deleteServerMutation = useMutation({
    mutationFn: async (data: { id: number }) => {
      return request("manager/del-admin-server/", data);
    },
    onSuccess: (data) => {
      // queryClient.invalidateQueries({ queryKey: ["admin-server-list"] });
    },
    onSettled: (data, error, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ["admin-server-list"] });
    },
  });

  /** 查询自有服务器的IP */
  const {
    data: ipListData,
    isFetching: ipListIsFetching,
    isError: ipListIsError,
    refetch: refetchIpList,
  } = useQuery({
    queryKey: ["admin-server-ip-list", { page: ipTableParams.pagination?.current, page_size: ipTableParams.pagination?.pageSize, server_id: checkingServerId }],
    queryFn: async ({ queryKey, signal }) => {
      const [_key, data] = queryKey;
      return request("manager/admin-server-ip-list/", data, signal);
    },
    enabled: !!checkingServerId,
  });

  /** 导入自有服务器IP */
  const importServerIpMutation = useMutation({
    mutationFn: async (data: { server_id: number; server_ip_infos: { ip: string; is_sold: number }[] }) => {
      return request("manager/admin-server-import-ip/", data);
    },
    onSuccess: (data) => {
      // queryClient.invalidateQueries({ queryKey: ["admin-server-list"] });
    },
    onSettled: (data, error, variables, context) => {
      setImportingServerId(null);
      setCsvData([]);
      setIsShowImportModal(false);
      queryClient.invalidateQueries({ queryKey: ["admin-server-list"] });
    },
  });

  const columns: ColumnsType<AdminServerItem> = [
    {
      title: "服务器名称",
      dataIndex: "server_name",
      key: "server_name",
    },
    {
      title: "IP",
      dataIndex: "server_ip",
      key: "server_ip",
    },
    {
      title: "Port",
      dataIndex: "port",
      key: "port",
    },
    {
      title: "Username",
      dataIndex: "username",
      key: "username",
    },
    {
      title: "Password",
      dataIndex: "pass",
      key: "pass",
    },
    {
      title: "product_id",
      dataIndex: "product_id",
      key: "product_id",
    },
    {
      title: "总IP数",
      dataIndex: "allIpCount",
      key: "allIpCount",
      render(value, record, index) {
        return (
          <>
            <FileSearchOutlined
              onClick={() => {
                queryClient.cancelQueries({ queryKey: ["admin-server-ip-list"] });
                setCheckingServerId(record.id);
                setIsShowIpListModal(true);
              }}
            />
            {record.allIpCount}
          </>
        );
      },
    },
    {
      title: "可售IP数",
      dataIndex: "soldIpCount",
      key: "soldIpCount",
    },
    {
      title: "备注",
      dataIndex: "remark",
      key: "remark",
    },
    {
      title: "创建时间",
      dataIndex: "create_time",
      key: "create_time",
    },
    {
      title: "操作",
      dataIndex: "id",
      key: "id",
      render(value: number, record) {
        return (
          <div className="tableOperation">
            <UploadOutlined
              onClick={() => {
                setImportingServerId(value);
                setCsvData([]);
                setIsShowImportModal(true);
              }}
            />
            <EditOutlined
              onClick={() => {
                form.setFieldsValue(record);
                setModalType(EModalType.UPDATE);
                setIsShowModal(true);
              }}
            />
            <DeleteOutlined
              onClick={() => {
                Modal.confirm({
                  title: "确认删除？",
                  onOk: () => {
                    deleteServerMutation.mutate({ id: value });
                  },
                });
              }}
            />
          </div>
        );
      },
    },
  ];

  const handleTableChange = (pagination: TablePaginationConfig) => {
    setTableParams({
      ...tableParams,
      pagination: {
        ...tableParams.pagination,
        ...pagination,
      },
    });
  };

  const handleUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const csvText = event.target?.result as string;
      const rows = csvText.split("\n").map((row) => row.split(","));
      setCsvData(rows);
    };
    reader.readAsText(file);
  };

  if (isError) {
    return <div>API Error</div>;
  }

  return (
    <main className="main">
      <div>Manage User Server</div>
      <div className="operationBar">
        <Button
          onClick={() => {
            setModalType(EModalType.ADD);
            setIsShowModal(true);
          }}
          type="primary"
        >
          新增服务器
        </Button>
      </div>
      <Table
        dataSource={data ? (data?.data?.data as AdminServerItem[]) : []}
        columns={columns}
        loading={isFetching}
        rowKey={"id"}
        pagination={{
          pageSize: data?.data.page_size,
          current: data?.data.page,
          total: data?.data.count,
          showTotal: (total) => `Total ${total} items`
        }}
        onChange={handleTableChange}
      />
      <Modal
        title={`${modalType === EModalType.ADD ? "新增" : "编辑"}服务器`}
        open={isShowModal}
        onCancel={() => {
          form.resetFields();
          setIsShowModal(false);
        }}
        onOk={() => {
          const values = form.getFieldsValue();
          if (modalType === EModalType.ADD) {
            addServerMutation.mutate(values);
          } else if (modalType === EModalType.UPDATE) {
            updateServerMutation.mutate(values);
          }
        }}
        okButtonProps={{
          loading: addServerMutation.isPending || updateServerMutation.isPending,
        }}
      >
        <Form form={form} labelCol={{ span: 4 }} wrapperCol={{ span: 20 }}>
          {modalType === EModalType.UPDATE && (
            <Form.Item<AdminServerItem> label="id" name="id">
              <Input disabled />
            </Form.Item>
          )}
          <Form.Item<AdminServerItem> label="服务器名称" name="server_name">
            <Input />
          </Form.Item>
          <Form.Item<AdminServerItem> label="服务器IP" name="server_ip">
            <Input />
          </Form.Item>
          <Form.Item<AdminServerItem> label="Port" name="port">
            <Input />
          </Form.Item>
          <Form.Item<AdminServerItem> label="Username" name="username">
            <Input />
          </Form.Item>
          <Form.Item<AdminServerItem> label="Password" name="pass">
            <Input />
          </Form.Item>
          <Form.Item<AdminServerItem> label="product_id" name="product_id">
            <Input />
          </Form.Item>
          <Form.Item<AdminServerItem> label="备注" name="remark">
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={"导入IP"}
        open={isShowImportModal}
        onCancel={() => {
          setIsShowImportModal(false);
        }}
        onOk={() => {
          importServerIpMutation.mutate({
            server_id: importingServerId as number,
            server_ip_infos: csvData.map((row) => ({
              ip: row[0],
              is_sold: Number(row[1]),
            })),
          });
        }}
        okButtonProps={{
          loading: importServerIpMutation.isPending,
        }}
      >
        <Upload
          accept=".csv"
          showUploadList={false}
          customRequest={(options) => {
            handleUpload(options.file as File);
          }}
        >
          <Button type="primary" icon={<UploadOutlined />}>
            Click to Upload
          </Button>
        </Upload>

        <table>
          {csvData.length > 0 && (
            <tbody>
              {csvData.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {row.map((cell, cellIndex) => (
                    <td key={cellIndex}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          )}
        </table>
      </Modal>

      <Modal
        title={"IP列表"}
        open={isShowIpListModal}
        footer={null}
        onCancel={() => {
          setIsShowIpListModal(false);
        }}
      >
        <Table
          dataSource={ipListData ? (ipListData?.data?.data as AdminServerItem[]) : []}
          columns={[
            {
              title: "ID",
              dataIndex: "id",
              key: "id",
            },
            {
              title: "IP",
              dataIndex: "ip",
              key: "ip",
            },
            {
              title: "是否售卖",
              dataIndex: "is_sold",
              key: "is_sold",
              render(value, record, index) {
                return value === 1 ? "是" : "否";
              },
            },
          ]}
          loading={ipListIsFetching}
          rowKey={"id"}
          pagination={{
            pageSize: ipListData?.data.page_size,
            current: ipListData?.data.page,
            total: ipListData?.data.count,
          }}
          onChange={(pagination) => {
            setIpTableParams({
              ...ipTableParams,
              pagination: {
                ...ipTableParams.pagination,
                ...pagination,
              },
            });
          }}
        />
      </Modal>
    </main>
  );
};

export default AdminServerManage;
